import { Image, Typography } from 'antd'
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint'
import { SeeMore } from 'components/shared/SeeMore'
import colors from 'helpers/colors'
import { EventType } from 'hooks/events'
import { useLottery } from 'hooks/lotteries'
import { generateDateTerm } from 'libs/generateDate'
import { getArtistName } from 'libs/getArtistName'
import { first, slice } from 'lodash'
import { useTranslation } from 'next-i18next'
import { FC, useEffect, useState } from 'react'

import { CustomerRankSection } from './parts/CustomerRankSection'
import { DateTermSection } from './parts/DateTermSection'
import { DateTermSectionForLottery } from './parts/DateTermSectionForLottery'
import { DescriptionSection } from './parts/DescriptionSection'
import { EntryButton } from './parts/EntryButton'
import { GoogleMapSection } from './parts/GoogleMapSection'
import { OwnerNameSection } from './parts/OwnerNameSection'

type Props = {
  event: EventType
  artists?: ArtistNameType[]
}

export const EventInformation: FC<Props> = ({ event, artists }) => {
  const { xs } = useBreakpoint()
  const { t, i18n } = useTranslation()

  const [isDisplayMoreArtists, setIsDisplayMoreArtists] = useState(false)
  const [isExpandInformation, setIsExpandInformation] = useState<boolean>()

  useEffect(() => {
    // モバイル版の画面の場合の初期値が閉じた状態になる
    setIsExpandInformation(!xs)
  }, [xs])

  const eventPlaces = first(event.event_places)
  const activeLottery = event.active_lottery
  const { data: activeLotteryData } = useLottery(activeLottery?.id)

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: xs ? 'column' : 'row',
        gap: '24px',
        paddingBottom: xs ? '32px' : '60px',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: xs ? '16px' : '40px',
          flex: 1,
        }}
      >
        <Image
          style={{ objectFit: 'cover' }}
          width="100%"
          height={xs ? '268px' : '490px'}
          src={event.main_event_image_url}
          loading="lazy"
          preview={false}
        />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            paddingBottom: '20px',
            padding: xs ? '0px 16px' : '0px',
          }}
        >
          <div style={{ display: 'flex', gap: 8 }}>
            {activeLottery && (
              <Typography.Text
                style={{
                  fontSize: '12px',
                  lineHeight: 1,
                  padding: '5px 8px',
                  textAlign: 'center',
                  color: colors.black,
                  background: colors.grey,
                }}
              >
                {t('エントリー')}
              </Typography.Text>
            )}
            <CustomerRankSection customerRank={event.customer_rank} />
          </div>
          <Typography.Text
            style={{
              fontSize: xs ? '22px' : '32px',
              lineHeight: 1.5,
              color: colors.primary,
            }}
          >
            {event.title}
          </Typography.Text>
          <Typography.Text
            style={{
              fontSize: xs ? '20px' : '28px',
              paddingBottom: xs ? 8 : 20,
              lineHeight: 1,
              color: colors.grey65,
            }}
          >
            {generateDateTerm({
              start: event.start_at,
              end: event.end_at,
              language: i18n.language,
            })}
          </Typography.Text>
          <DescriptionSection
            description={event.description}
            isExpandInformation={!!isExpandInformation}
            setIsExpandInformation={() =>
              setIsExpandInformation((prev) => !prev)
            }
          />
          <div style={{ marginTop: 24 }}>
            <EntryButton xs={xs} activeLottery={activeLottery} />
          </div>
        </div>
      </div>
      {(!xs || isExpandInformation) && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: xs ? '16px' : '28px',
            width: xs ? '100%' : '262px',
            padding: xs ? '0px 16px' : '0px',
          }}
        >
          <OwnerNameSection
            ownerId={event.owner.id}
            ownerName={event.owner.name}
          />
          <DateTermSection startAt={event.start_at} endAt={event.end_at} />
          {activeLotteryData && (
            <DateTermSectionForLottery
              startAt={activeLotteryData.start_at}
              endAt={activeLotteryData.end_at}
              announceAt={activeLotteryData.announce_at}
            />
          )}
          {/* TODO: プレビュー期間の設定 */}
          {/* <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <Typography.Text ellipsis style={{ fontSize: "12px", lineHeight: 1.5, color: colors.grey30 }}>
          プレビュー期間
          </Typography.Text>
          <Typography.Text style={{ fontSize: "14px", lineHeight: 1.5, color: colors.grey65 }}>
          期間を表示
          </Typography.Text>
        </div> */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Typography.Text
              style={{
                fontSize: '12px',
                lineHeight: 1.5,
                color: colors.grey30,
              }}
            >
              {t('アーティスト')}
            </Typography.Text>
            {artists && (
              <>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    marginTop: '12px',
                  }}
                >
                  {(isDisplayMoreArtists ? artists : slice(artists, 0, 5)).map(
                    (artist) => {
                      return (
                        <Typography.Text
                          key={artist.id}
                          style={{
                            fontSize: '14px',
                            lineHeight: 1.5,
                            fontWeight: 500,
                            color: colors.grey65,
                          }}
                        >
                          {getArtistName(artist, i18n.language)}
                        </Typography.Text>
                      )
                    },
                  )}
                </div>
                {artists.length > 5 && (
                  <SeeMore
                    expanded={isDisplayMoreArtists}
                    setExpanded={setIsDisplayMoreArtists}
                  />
                )}
              </>
            )}
          </div>
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
          >
            <Typography.Text
              style={{
                fontSize: '12px',
                lineHeight: 1.5,
                color: colors.grey30,
              }}
            >
              {t('会場')}
            </Typography.Text>
            {eventPlaces ? (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Typography.Text
                  style={{
                    fontSize: '14px',
                    lineHeight: 1.5,
                    color: colors.grey65,
                  }}
                >
                  {eventPlaces.name}
                </Typography.Text>
                {eventPlaces.postal_code && (
                  <Typography.Text
                    style={{
                      fontSize: '14px',
                      lineHeight: 1.5,
                      color: colors.grey65,
                    }}
                  >
                    {`〒${eventPlaces.postal_code.substring(
                      0,
                      3,
                    )}-${eventPlaces.postal_code.substring(3, 7)}`}
                  </Typography.Text>
                )}
                <Typography.Text
                  style={{
                    fontSize: '14px',
                    lineHeight: 1.5,
                    color: colors.grey65,
                  }}
                >
                  {eventPlaces.state} {eventPlaces.city} {eventPlaces.address1}
                </Typography.Text>
                <Typography.Text
                  style={{
                    fontSize: '14px',
                    lineHeight: 1.5,
                    color: colors.grey65,
                  }}
                >
                  {eventPlaces.address2}
                </Typography.Text>
                <div style={{ marginTop: '12px' }}>
                  <GoogleMapSection eventAddress={eventPlaces.address} />
                </div>
              </div>
            ) : (
              <Typography.Text
                style={{
                  fontSize: '14px',
                  lineHeight: 1.5,
                  color: colors.grey65,
                }}
              >
                {t('未設定')}
              </Typography.Text>
            )}
          </div>
          {/* TODO: 営業時間の設定 */}
          {/* <div style={{ display: "flex", flexDirection: "column", gap: "12px", paddingBottom: "28px" }}>
          <Typography.Text ellipsis style={{ fontSize: "12px", lineHeight: 1.5, color: colors.grey30 }}>
          営業時間
          </Typography.Text>
          <Typography.Text style={{ fontSize: "14px", lineHeight: 1.5, color: colors.grey65 }}>
          時間を表示
          </Typography.Text>
        </div> */}
        </div>
      )}
    </div>
  )
}
